import { useSession } from "@clerk/clerk-react";
import { Clock, MegaphoneSimple, MegaphoneSimpleIcon } from "@phosphor-icons/react";
import { ArrowUpRight } from "assets/icons";
import { API_URL } from "constants/index";
import { useEffect, useState } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

interface SlackMessage {
  ts: number;
  userAvatar?: string;
  userName?: string;
  text: string;
  channel: string;
}

interface SlackFeedProps {
  channelName: string;
  channelId: string;
}

const formatDate = (timestamp: number) => {
  const date = new Date(timestamp * 1000); // Convert to milliseconds
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  };
  const formattedDate = new Intl.DateTimeFormat("nl-BE", options).format(date);
  return formattedDate.replace(":", "u");
};

const generateSlackUrl = (message: SlackMessage) => {
  const workspace = "yallyworkspace";
  const timestamp = message.ts.toString().replace(".", "");
  return `https://${workspace}.slack.com/archives/${message.channel}/p${timestamp}`;
};

const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const parseMessageForLinks = (text: any) => {
  const linkRegex = /<([^>]+)>/g;
  return text.split(linkRegex).map((part: any, index: any) => {
    if (index % 2 === 0) {
      // Non-link text
      return part;
    } else {
      // Link text
      return (
        <a
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-moss-primary" // Applying the Tailwind class for styling
        >
          {part}
        </a>
      );
    }
  });
};

export function SlackFeed({ channelName, channelId }: SlackFeedProps) {
  const [messages, setMessages] = useState<SlackMessage[]>([]);
  const { session } = useSession();

  useEffect(() => {
    async function fetchMessages() {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/slack/channel/${channelName}`,
          { method: "GET" },
          session,
        );
        const data = await response.json();
        setMessages(data.slice(0, 5));
      } catch (err) {
        console.error("Oops, failed to fetch messages:", err);
      }
    }

    fetchMessages();
  }, [channelName]);

  const slackChannelLink = `https://yallyworkspace.slack.com/archives/${channelId}`;
  const formattedChannelName = capitalizeFirstLetter(channelName);

  return (
    <div className="flex flex-col gap-2.5">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <MegaphoneSimpleIcon weight="duotone" size={22} />
          <p className="text-lg">{formattedChannelName}</p>
        </div>
        <a
          href={slackChannelLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-0.5 text-sm font-medium text-moss-primary"
        >
          Open Slack <ArrowUpRight className="h-[18px] w-[18px]" />
        </a>
      </header>
      <div className="relative overflow-hidden rounded-lg bg-muted shadow-sm">
        <div className="divide-y divide-neutral-primary/20">
          {messages.map((message, index) => (
            <div
              key={index}
              className="relative z-1 flex gap-2.5 p-2.5 text-sm"
            >
              {message.userAvatar && (
                <img
                  src={message.userAvatar}
                  alt={message.userName || "User"}
                  className="h-8 w-8 rounded-full"
                />
              )}
              <div className="flex flex-col gap-1">
                <div className="flex flex-col">
                  <p className="font-medium">
                    {message.userName || "Unknown User"}
                  </p>
                  <p className="text-muted-foreground line-clamp-4">
                    {parseMessageForLinks(message.text)}
                  </p>
                </div>
                <a
                  href={generateSlackUrl(message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-neutral-secondary-mid"
                >
                  <Clock weight="duotone" size={15} />
                  {formatDate(message.ts)}
                </a>
              </div>
            </div>
          ))}
        </div>
        <div className="absolute bottom-0 right-0 z-0 translate-x-1/4 translate-y-1/4 text-brick-primary/12">
          <MegaphoneSimple weight="duotone" size={120} />
        </div>
      </div>
    </div>
  );
}
